import { useRef, useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { addDays, addWeeks, startOfWeek, format } from "date-fns";
import { Calendar } from "lucide-react";

export default function SmartDatePicker({
  open,
  setOpen,
  selected,
  setSelected,
  placement = "bottom",
  align = "right",
}) {
  const pickerRef = useRef(null);

  const [tempDate, setTempDate] = useState(null);
  const [hour, setHour] = useState(6);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState("AM");

  useEffect(() => {
    function handleClickOutside(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener("pointerdown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) return;

    if (!selected) {
      setTempDate(null);
      setHour(6);
      setMinute(0);
      setPeriod("AM");
      return;
    }

    const dateObj =
      selected instanceof Date ? selected : new Date(selected);

    if (isNaN(dateObj)) return;

    let h = dateObj.getHours();
    const m = dateObj.getMinutes();

    setPeriod(h >= 12 ? "PM" : "AM");
    h = h % 12 || 12;

    setHour(h);
    setMinute(m);
    setTempDate(dateObj);
  }, [open, selected]);

  const applyDateTime = (date) => {
    if (!date) {
      setSelected(null);
      setOpen(false);
      return;
    }

    let finalHour = hour % 12;
    if (period === "PM") finalHour += 12;

    const finalDate = new Date(date);
    finalDate.setHours(finalHour);
    finalDate.setMinutes(minute);
    finalDate.setSeconds(0);
    finalDate.setMilliseconds(0);

    setSelected(finalDate);
    setOpen(false);
  };

  const shortcuts = [
    { label: "Today", value: new Date() },
    { label: "Tomorrow", value: addDays(new Date(), 1) },
    {
      label: "This weekend",
      value: addDays(new Date(), 6 - new Date().getDay()),
    },
    {
      label: "Next week",
      value: addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), 1),
    },
    { label: "2 weeks", value: addWeeks(new Date(), 2) },
    { label: "4 weeks", value: addWeeks(new Date(), 4) },
    { label: "Clear", value: null },
  ];

  const positionClass =
    placement === "top" ? "bottom-full mb-2" : "top-full mt-2";

  const alignClass =
    align === "right"
      ? "right-0"
      : align === "center"
        ? "left-1/2 -translate-x-1/2"
        : "left-0";

  return (
    <div className="relative inline-flex text-black">
      <div
        onClick={() => setOpen(!open)}
        className="
          cursor-pointer flex items-center gap-2
          border border-gray-300
          rounded sm:rounded-lg px-2 md:px-3 py-1
          bg-white
          shadow-sm
          text-[11px] md:text-sm
          transition-all duration-200
        "
      >
        <Calendar className="w-4 h-4 text-gray-500" />

        {selected ? (
          <span className="text-gray-900 font-medium">
            {format(
              selected instanceof Date ? selected : new Date(selected),
              "dd MMM yyyy hh:mm a"
            )}
          </span>
        ) : (
          <span className="text-gray-400 text-[12px] sm:text-[15px]">
            Select date
          </span>
        )}
      </div>

      {open && (
        <div
          ref={pickerRef}
          className={`
            absolute ${positionClass} ${alignClass}
            bg-white
            border border-gray-200
            rounded-2xl
            shadow-xl
            z-[999999999]
            flex
            overflow-hidden
          `}
        >
          <div className="hidden sm:flex w-40 border-r border-gray-100 flex-col bg-gray-50">
            {shortcuts.map((s) => (
              <div
                key={s.label}
                className="
                  px-4 py-2 text-sm
                  cursor-pointer
                  text-gray-700
                  hover:bg-white hover:text-black
                  transition-all
                "
                onClick={() => {
                  if (s.value === null) {
                    applyDateTime(null);
                  } else {
                    setTempDate(s.value);
                  }
                }}
              >
                {s.label}
              </div>
            ))}
          </div>

          {/* Right Content */}
          <div className="p-2 sm:p-4 min-w-[260px]">
            {/* Time Picker */}
            <div className="flex items-center gap-2">
              <select
                value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
                className="
                  border border-gray-200
                  px-2 py-1.5
                  rounded-lg
                  text-sm
                  focus:outline-none
                  focus:ring-2 focus:ring-black/20
                "
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>

              <span className="text-gray-500 text-sm">:</span>

              <select
                value={minute}
                onChange={(e) => setMinute(Number(e.target.value))}
                className="
                  border border-gray-200
                  px-2 py-1.5
                  rounded-lg
                  text-sm
                  focus:outline-none
                  focus:ring-2 focus:ring-black/20
                "
              >
                {[0, 15, 30, 45].map((m) => (
                  <option key={m} value={m}>
                    {m.toString().padStart(2, "0")}
                  </option>
                ))}
              </select>

              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="
                  border border-gray-200
                  px-2 py-1.5
                  rounded-lg
                  text-sm
                  focus:outline-none
                  focus:ring-2 focus:ring-black/20
                "
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>

            {/*  Calendar */}
            <div className="mt-3 rounded-xl border border-gray-100 p-2">
              <DayPicker
                mode="single"
                selected={tempDate}
                onSelect={(date) => setTempDate(date)}
                disabled={{ before: new Date() }}
              />
            </div>

            {/*  Apply Button */}
            <button
              onClick={() => applyDateTime(tempDate)}
              className="
                mt-4 w-full
                bg-black text-white
                py-2
                rounded-xl
                text-sm font-medium
                hover:bg-gray-900
                transition-all
                active:scale-[0.98]
              "
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}